// What each calendar entry says (screens/CALENDAR.md, "What is on a day" and
// "Copy"): a card in the day it falls on that says in words what it is, a
// word beside its time when one matters, and where it opens. Facts only:
// nothing here says whom to play (DIRECTION U3).
//
// THE CARD SAYS IT, NOT A MARK: a cell carries the names, the claims and the
// teams, so a quick look reads what is coming without opening anything
// (DIRECTION U30). Its tone is what it asks of the reader — mine, due by its
// time, the league's, or a lineup that cannot score — and is drawn in color
// beside the words, never instead of them.
//
// SIMPLE LEAVES OUT THE NUMBERS, never adds a sentence (DETAIL_LEVELS.md):
// `numbers` false drops a count of starters or picks, and nothing is said in
// its place.

import type { CalendarEvent, CalendarPlayer, CalendarWeek, TeamRef } from '@fsp/domain';
import type { IconName } from '../../kit/Icon';

/** The server's kinds, and a week's lineup that cannot score, placed by the screen. */
export type EntryKind = CalendarEvent['kind'] | 'lineup';

/**
 * What an entry asks of the reader, drawn as its edge: `mine` my players and
 * my own things, `due` something to do or decide by its time, `league` what
 * happens to everybody, `alert` a lineup with a slot that cannot score.
 */
export type EntryTone = 'mine' | 'due' | 'league' | 'alert';

export interface Entry {
  key: string;
  kind: EntryKind;
  at: number;
  icon: IconName;
  /** The full line, for Coming up and an empty day's next. */
  title: string;
  /** What the card says it is, bold under its time: "Game time", "Waivers run". */
  card: string | null;
  /** A word beside the time: "Early", "Answer due". */
  tag: string | null;
  /** The lines under the card's name. */
  lines: string[];
  tone: EntryTone;
  /** It waits on the reader, so it is filled in. */
  needsMe: boolean;
  /** Placed on a day rather than at an instant, so it shows no time. */
  timeless: boolean;
  /** Where it opens, after the league's address; null for nowhere. */
  to: string | null;
  /** What opening it says: "Open Trades". */
  open: string | null;
  /** My players, for a kickoff. */
  players: CalendarPlayer[];
  /** A kickoff before the week's main games. */
  early: boolean;
  /** Its players lock at it. */
  locks: boolean;
}

/** "8:15 PM", in the reader's zone. */
export function clockIn(at: number, zone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: zone,
  }).format(new Date(at));
}

/** "Thursday, Oct 30". */
export function dayLabelIn(at: number, zone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: zone,
  }).format(new Date(at));
}

/** "Thu 8:15 PM", for Coming up and the phone's next line. */
export function whenIn(at: number, zone: string): string {
  const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: zone }).format(
    new Date(at),
  );
  return `${day} ${clockIn(at, zone)}`;
}

/** "Jefferson and Hall", "Jefferson, Hall and 2 more". */
function namesOf(players: readonly CalendarPlayer[], numbers: boolean): string {
  const names = players.map((p) => p.player.shortName);
  if (names.length <= 2) return names.join(' and ');
  return numbers
    ? `${names.slice(0, 2).join(', ')} and ${String(names.length - 2)} more`
    : `${names.slice(0, 2).join(', ')} and more`;
}

function teamName(team: TeamRef): string {
  return team.name;
}

/** A week's line: its name and whom I play, both in a week of two games (Q6). */
export function weekLine(week: CalendarWeek): string {
  if (week.opponent === null) return week.label;
  const second = week.secondOpponent === null ? '' : ` and ${teamName(week.secondOpponent)}`;
  return `${week.label} · vs ${teamName(week.opponent)}${second}`;
}

/** A week's flag: its problems that cost points, in the lineup's own words. */
export function problemLine(week: CalendarWeek): string | null {
  if (week.problems.length === 0) return null;
  return week.problems.map((p) => p.message).join(' ');
}

/**
 * A WEEK'S LINEUP THAT CANNOT SCORE, as a card of its own on the day to fix
 * it: today while the week is under way, or the day it first locks while it
 * is to come, and none once its last game has started. Each problem in the
 * lineup's own words, so the calendar and My Team cannot disagree.
 */
export function lineupEntry(week: CalendarWeek, now: number): Entry | null {
  const line = problemLine(week);
  if (line === null) return null;
  const firstLock = Date.parse(week.firstLockAt ?? week.start);
  const last = week.slate[week.slate.length - 1];
  const closes = last === undefined ? Date.parse(week.end) : Date.parse(last.at);
  const at = Math.max(now, firstLock);
  if (at >= closes) return null;
  return {
    key: `lineup|${String(week.period)}`,
    kind: 'lineup',
    at,
    icon: 'warning',
    title: `${week.label}: ${line}`,
    card: `Your ${week.label} lineup`,
    tag: null,
    lines: week.problems.map((p) => p.message),
    tone: 'alert',
    needsMe: true,
    timeless: true,
    to: `?period=${String(week.period)}`,
    open: 'Open My Team',
    players: [],
    early: false,
    locks: false,
  };
}

export function entryOf(e: CalendarEvent, weeks: readonly CalendarWeek[], numbers: boolean): Entry {
  const at = Date.parse(e.at);
  const base: Base = {
    kind: e.kind,
    key: `${e.kind}|${e.at}|${'tradeId' in e ? e.tradeId : ''}${'what' in e ? e.what : ''}${'vote' in e ? e.vote : ''}`,
    at,
    tag: null,
    lines: [],
    needsMe: false,
    timeless: false,
    to: null,
    open: null,
    players: [],
    early: false,
    locks: false,
  };
  switch (e.kind) {
    case 'games': {
      const pre = e.players.some((p) => p.state === 'pre');
      const hurt = e.players.some(
        (p) => p.slotKind === 'starter' && (p.health === 'out' || p.health === 'warn'),
      );
      const names = namesOf(e.players, numbers);
      return {
        ...base,
        icon: 'sport',
        title: `${names} ${e.players.length === 1 ? 'plays' : 'play'}`,
        card: 'Game time',
        tag: e.early ? 'Early' : null,
        tone: e.early && pre ? 'due' : 'mine',
        needsMe: pre && (e.early || hurt),
        to: e.period === null ? '/matchup' : `/matchup?period=${String(e.period)}`,
        open: 'Open Game Day',
        players: e.players,
        early: e.early,
        locks: e.locks,
      };
    }
    case 'lineup-lock': {
      const week = weeks.find((w) => w.period === e.period);
      const label = week?.label ?? 'the week';
      return {
        ...base,
        icon: 'locked',
        title: e.starters === null ? `Lineups lock for ${label}` : `Your lineup locks for ${label}`,
        card: e.starters === null ? 'Lineups lock' : 'Your lineup locks',
        lines:
          numbers && e.starters !== null
            ? [`${String(e.starters)} starting ${e.starters === 1 ? 'slot' : 'slots'}`]
            : [],
        tone: 'due',
        needsMe: (week?.problems.length ?? 0) > 0,
        to: e.period === null ? '' : `?period=${String(e.period)}`,
        open: 'Open My Team',
        locks: true,
      };
    }
    case 'waivers': {
      const claims = e.claims ?? [];
      const names = claims.map((p) => p.shortName).join(', ');
      return {
        ...base,
        icon: 'waivers',
        title: e.continuous ? `Claim decided: ${names}` : 'Waivers run',
        card: e.continuous ? 'Claim decided' : 'Waivers run',
        lines: e.continuous
          ? [names]
          : e.claims === null
            ? []
            : claims.length === 0
              ? ['No claims of yours']
              : [`Your ${claims.length === 1 ? 'claim' : 'claims'}: ${names}`],
        tone: claims.length > 0 ? 'mine' : 'league',
        to: '/waivers',
        open: 'Open Waivers',
      };
    }
    case 'trade':
      return tradeEntry(e, base);
    case 'deadline':
      return deadlineEntry(e, base);
    case 'draft':
      return {
        ...base,
        icon: 'draft',
        title: 'The draft',
        card: 'The draft',
        lines: [
          e.roomOpensAt !== null
            ? 'The room opens half an hour before'
            : e.mode === 'autopick'
              ? 'It runs by itself, from each team’s rankings'
              : 'The commissioner enters the picks',
        ],
        tone: 'due',
        needsMe: true,
        to: '/draft',
        open: e.mode === 'live' ? 'Open the draft room' : 'Open the draft',
      };
    case 'week-ends': {
      const week = weeks.find((w) => w.period === e.period);
      const label = week?.label ?? 'The week';
      return {
        ...base,
        icon: 'recap',
        title: `${label} ends`,
        card: `${label} ends`,
        lines: ['Results, awards and the recap'],
        tone: 'league',
        to: '/recap',
        open: 'Open the recap',
      };
    }
    case 'playoffs':
      return {
        ...base,
        icon: 'podium',
        title: 'The playoffs begin',
        card: 'The playoffs begin',
        tone: 'league',
        to: '/schedule?tab=playoffs',
        open: 'Open the playoffs',
      };
    case 'vote':
      return {
        ...base,
        icon: 'veto',
        title: e.vote === 'rules' ? `Rule vote closes: ${e.text}` : 'Commissioner election closes',
        card: e.vote === 'rules' ? 'Rule vote closes' : 'Election closes',
        tag: e.voted ? null : 'Not voted',
        lines: e.vote === 'rules' ? [e.text] : [],
        tone: e.voted ? 'league' : 'due',
        needsMe: !e.voted,
        to: e.vote === 'rules' ? '/rules' : '/managers',
        open: e.vote === 'rules' ? 'Open the rules' : 'Open Managers',
      };
    case 'pickem':
      return {
        ...base,
        icon: 'favorite',
        title: 'Pick’em locks',
        card: 'Pick’em locks',
        lines: numbers ? [`${String(e.picked)} of ${String(e.of)} picked`] : [],
        tone: e.picked < e.of ? 'due' : 'league',
        needsMe: e.picked < e.of,
        to: '/pickem',
        open: 'Open pick’em',
      };
  }
}

type Base = Omit<Entry, 'icon' | 'title' | 'card' | 'tone'>;

function tradeEntry(e: Extract<CalendarEvent, { kind: 'trade' }>, base: Base): Entry {
  const other = teamName(e.team);
  const common = {
    ...base,
    icon: 'trade' as const,
    lines: [e.summary],
    to: '/trades',
    open: 'Open Trades',
  };
  if (e.step === 'answer')
    return {
      ...common,
      title: `Offer from ${other}: your answer is due`,
      card: `Offer from ${other}`,
      tag: 'Answer due',
      tone: 'due',
      needsMe: true,
    };
  if (e.step === 'awaiting')
    return {
      ...common,
      title: `Your offer to ${other}: their answer is due`,
      card: `Your offer to ${other}`,
      tag: 'Their answer due',
      tone: 'mine',
    };
  if (e.step === 'review')
    return {
      ...common,
      title: 'Trade review ends: it goes through unless vetoed',
      card: 'Trade review ends',
      tag: e.canVote ? 'You can vote' : null,
      lines: [e.summary, 'It goes through unless vetoed'],
      tone: e.canVote ? 'due' : 'league',
      needsMe: e.canVote,
    };
  return {
    ...common,
    title: 'Trade goes through',
    card: 'Trade goes through',
    tone: e.mine ? 'mine' : 'league',
  };
}

function deadlineEntry(e: Extract<CalendarEvent, { kind: 'deadline' }>, base: Base): Entry {
  const common = { ...base, icon: 'pending' as const, tone: 'due' as const, needsMe: true };
  if (e.what === 'trades')
    return {
      ...common,
      title: 'Trade deadline',
      card: 'Trade deadline',
      lines: ['Offers still open are voided'],
      to: '/trades',
      open: 'Open Trades',
    };
  if (e.what === 'taxi')
    return {
      ...common,
      title: 'Taxi squad closes',
      card: 'Taxi squad closes',
      lines: ['Nobody goes onto the taxi squad after this'],
      to: '',
      open: 'Open My Team',
    };
  return {
    ...common,
    title: 'Keepers lock',
    card: 'Keepers lock',
    lines: ['Your keepers are final from here'],
    to: '/keepers',
    open: 'Open Keepers',
  };
}
